import { useEffect, useState } from 'react';
import { Link, useRecordContext, type RaRecord } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Chip,
  type ChipProps,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { SectionCard, fmtDay } from '../../shared/Panel';
import { clientDayKey, shortDateTime } from '../../shared/dateTime';
import { TABLE_SCROLL_SX } from '../../shared/tableScroll';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { recordScoped } from '../../shared/recordScoped';
import { plural } from '../../shared/plural';
import {
  fetchDriverActivity,
  fetchVehiclePlan,
  withoutTmsPlan,
  type DriverActivity,
  type PlanItem,
  type VehiclePlan,
} from './vehiclePlanApi';
import { BracketLegend, LegendSwatch, PHASE_FILL, segmentFill, tmsPlanFill } from './PlanTimeline';
import { routeText, shiftDayKey } from './timeline';
import {
  PLANNED_SHOWN,
  arrangePlanRows,
  notVariantLabel,
  pastWindow,
  planRowStatus,
  rangeText,
  runningInTms,
  tmsStatusText,
  type PlanRowState,
} from './planRows';
import { scheduleStatusLabel } from './carScheduleApi';
import { LEGEND_SEGMENTS, durationText, hasSegments } from './planSegments';
import { TipLayer } from './GanttTips';
import { ACTIVITY_COLORS, ACTIVITY_LABELS, VehicleGantt, alertTipLines } from './VehicleGantt';
import { useCarCard } from './CarCardContext';
import { useFeature } from '../featureFlags/FeatureGate';
import {
  GANTT_WINDOW_BRACKETS,
  OCCUPIED_UNTIL_TMS_PLAN,
  RUNNING_ORDER_NOT_VARIANT,
  VEHICLE_VARIANTS_LANE,
} from './fleetFeatures';
import {
  homeStartKey,
  isScale,
  lateSpan,
  orderWindows,
  rangeLabel,
  readGanttPrefs,
  rescaleStartKey,
  scaleOptions,
  scrollStep,
  viewKeys,
  viewWindow,
  writeGanttPrefs,
  type GanttDetail,
  type ScaleDays,
} from './ganttRules';

/**
 * Harmonogram pojazdu — obsadzenia i warianty na osi dni, tylko odczyt.
 *
 * ## Po co ta sekcja
 *
 * Status z 29.09.2026: „finalne przypisanie pojazdu do zlecenia musi być
 * odzwierciedlone w planie pracy floty, widoczne np. w harmonogramie danego
 * pojazdu” — planiści pracują z listy aut. Plan bierze się **ze zleceń**:
 * obsadzenia (pewne) i warianty (wstępne). Okno miesięczne zostaje
 * kontekstem — serwis widać jako zakreskowanie.
 *
 * ## Od 07.10.2026 — prototyp p. Mariusza
 *
 * Oś przebudowana według prototypu `pojazd-BQO1011-v2.html` (odstępstwa:
 * `UWAGI.md` przy prototypie): wiersze „W trakcie / Zaplanowane /
 * Zakończone”, zlecenie jako okno Z + realizacja + okno R, widok uproszczony
 * i pełny, skala 1 / 7 / 14 / 21 / 28 dni z zachowaniem środka kadru,
 * podziałka godzinowa, wiersz aktywności kierowcy z tachografu
 * z tygodniowym czasem jazdy, ikona alertu na pasku i w tabeli, odcinek
 * spóźnienia za oknem rozładunku. Rysuje `VehicleGantt.tsx`, reguły —
 * `ganttRules.ts`. Pasek nawigacji: przyciski, pięć przycisków skali,
 * przełącznik widoku i zakres dat obok (zmiany 6–8); skalę i widok
 * pamiętamy w przeglądarce (zmiana 17).
 *
 * Tabela pod osią (zmiana 10): bez kolumny „Rodzaj” (wariant mówi „Stan”),
 * jedna kolumna okna, „Stan” obok numeru; najechanie na wiersz podświetla
 * pasek i odwrotnie (zmiana 11). Mieści się w karcie od 1366 px.
 */

/** Etykieta czipu w tabeli zawija się zamiast rozpychać kolumnę. */
const WRAPPING_CHIP_SX = {
  height: 'auto',
  maxWidth: 150,
  '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25, lineHeight: 1.25 },
} as const;

/**
 * Kolory stanu jak na osi (07.10.2026, prototyp): w realizacji niebieskie,
 * zaplanowane zielone, zrealizowane szare, wariant przerywany.
 */
const STATE_LOOK: Record<
  PlanRowState,
  { color: ChipProps['color']; variant: ChipProps['variant']; dashed?: boolean }
> = {
  inProgress: { color: 'primary', variant: 'outlined' },
  planned: { color: 'success', variant: 'outlined' },
  tentative: { color: 'success', variant: 'outlined', dashed: true },
  done: { color: 'default', variant: 'outlined' },
  unknown: { color: 'default', variant: 'outlined' },
};

/**
 * Stan wiersza (`planRows.ts`) — od 07.10.2026 także rodzaj: wariant mówi
 * „wstępnie zaplanowane”, a wariant zlecenia obsadzonego innym pojazdem —
 * wprost (dawna kolumna „Rodzaj”). Status z TMS w dymku.
 */
const StateChip = ({ item }: { item: PlanItem }) => {
  const { state, label } = planRowStatus(item);
  const look = STATE_LOOK[state];
  const elsewhere = item.kind === 'candidate' && item.assignedElsewhere;
  // S-24.10-06: kandydatura do zlecenia, które TMS ma w realizacji, nie jest wariantem.
  const runningNotVariant = useFeature(RUNNING_ORDER_NOT_VARIANT);
  const notVariant = runningNotVariant && runningInTms(item);
  return (
    <Chip
      size="small"
      color={elsewhere ? 'error' : look.color}
      variant={look.variant}
      label={notVariant ? notVariantLabel(item) : elsewhere ? 'wariant (zlecenie już obsadzone)' : label}
      data-testid={notVariant ? 'plan-state-not-variant' : 'plan-state-chip'}
      // Z klawiatury (prototyp, zmiana 9): fokus pokazuje dymek ze statusem TMS.
      tabIndex={0}
      data-tip={[
        tmsStatusText(item) ?? 'Brak statusu w TMS',
        ...(elsewhere ? ['Zlecenie jest już obsadzone innym pojazdem.'] : []),
      ].join('\n')}
      sx={{ ...WRAPPING_CHIP_SX, ...(look.dashed || elsewhere ? { borderStyle: 'dashed' } : {}) }}
    />
  );
};

/**
 * „Źródło” — skąd wiersz (omówienie 01.10.2026: „to nie jest stan, tylko …
 * proweniencja”). Reguła po stronie API (`planItemSource`); zlecenie spoza
 * TMS ma dopisek. Starsze API bez pola — kreska.
 */
const SourceCell = ({ item }: { item: PlanItem }) => {
  const s = item.source;
  if (!s) return <Box component="span" data-testid="plan-source">—</Box>;
  return (
    <Box component="span" data-tip={s.detail} data-testid="plan-source" sx={{ display: 'block' }}>
      {s.label}
      {s.orderOrigin ? (
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
          {s.orderOrigin}
        </Typography>
      ) : null}
    </Box>
  );
};

/** Kto obsadził — druga linia komórki „Źródło”; obsadzenie z importu — „w TMS”. */
const AssignedByLine = ({ item }: { item: PlanItem }) => {
  const a = item.assignedBy;
  if (!a) return null;
  return (
    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }} data-testid="plan-assigned-by">
      obsadził: <span>{`${a.username ?? (a.source === 'IMPORT' ? 'w TMS' : '—')}, ${shortDateTime(a.at)}`}</span>
    </Typography>
  );
};

/**
 * Jedna kolumna okna (zmiana 10): okno klienta jednym przedziałem; gdy plan
 * TMS ma inne terminy punktów — „Z:” i „R:” i okno klienta szarą linią.
 */
const WindowCell = ({ item }: { item: PlanItem }) => {
  const w = orderWindows(item);
  if (w.mode === 'client') return <span>{rangeText(item.windowFrom, item.windowTo)}</span>;
  return (
    <>
      {w.loading ? (
        <Box component="span" sx={{ display: 'block' }}>
          Z: {rangeText(w.loading.from, w.loading.to)}
        </Box>
      ) : null}
      {w.unloading ? (
        <Box component="span" sx={{ display: 'block' }}>
          R: {rangeText(w.unloading.from, w.unloading.to)}
        </Box>
      ) : null}
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
        okno klienta: {rangeText(item.windowFrom, item.windowTo)}
      </Typography>
    </>
  );
};

/** Zaplanowane z minionym oknem — reguła w `planRows.ts` (wspólna z panelem „Do zrobienia”). */
export { pastWindow };

/** Komórki tabeli: węższy margines niż domyślny, tekst zawija się u góry. */
const CELL_SX = { px: 1, verticalAlign: 'top' } as const;
const HEAD_SX = { ...CELL_SX, color: 'text.secondary' } as const;
const WARNING_ORANGE = '#ed6c02';

/**
 * Lista pod osią — od tego, co przed autem (omówienie 01.10.2026): w realizacji,
 * dwa najbliższe zaplanowane, potem ostatnie zrealizowane obsadzenie;
 * dalsze zaplanowane i wcześniejsze zrealizowane pod przyciskami.
 * Reguły: `planRows.ts`.
 */
const PlanList = ({
  items,
  highlight,
  onHighlight,
  now,
}: {
  items: PlanItem[];
  highlight: number | null;
  onHighlight: (orderId: number | null) => void;
  now: Date;
}) => {
  const [showEarlierDone, setShowEarlierDone] = useState(false);
  const [showAllPlanned, setShowAllPlanned] = useState(false);
  const { rows, hiddenDone, hiddenPlanned } = arrangePlanRows(items, showEarlierDone, showAllPlanned);
  return (
    <TipLayer>
      {rows.length > 0 ? (
        <Box sx={TABLE_SCROLL_SX}>
          <Table size="small" data-testid="plan-list" onMouseLeave={() => onHighlight(null)}>
            <TableHead>
              <TableRow>
                <TableCell sx={HEAD_SX}>Zlecenie</TableCell>
                <TableCell sx={HEAD_SX}>Stan</TableCell>
                <TableCell sx={HEAD_SX}>Relacja</TableCell>
                <TableCell sx={HEAD_SX}>Okno załadunku i rozładunku</TableCell>
                <TableCell sx={HEAD_SX}>Zajętość (szacunek)</TableCell>
                <TableCell sx={HEAD_SX}>Źródło</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((i) => {
                const late = lateSpan(i);
                const alerts = i.alerts ?? [];
                return (
                  <TableRow
                    key={`${i.kind}-${i.orderId}`}
                    data-testid="plan-list-row"
                    data-order={i.orderId}
                    selected={highlight === i.orderId}
                    onMouseEnter={() => onHighlight(i.orderId)}
                    onFocus={() => onHighlight(i.orderId)}
                  >
                    {/* Numer się nie łamie — to jedyna kolumna bez zawijania. */}
                    <TableCell sx={{ ...CELL_SX, whiteSpace: 'nowrap' }}>
                      {alerts.length > 0 ? (
                        <Box
                          component="span"
                          tabIndex={0}
                          role="img"
                          aria-label="Zlecenie z alertem"
                          data-testid={`plan-list-alert-${i.orderId}`}
                          data-tip={alertTipLines(alerts).join('\n\n')}
                          sx={{ display: 'inline-flex', verticalAlign: '-4px', mr: 0.75, cursor: 'help' }}
                        >
                          <WarningAmberRoundedIcon sx={{ fontSize: 20, color: WARNING_ORANGE }} />
                        </Box>
                      ) : null}
                      <Link to={`/user-offers/${i.orderId}/show`}>{i.number ?? `#${i.orderId}`}</Link>
                    </TableCell>
                    <TableCell sx={CELL_SX}>
                      <StateChip item={i} />
                      {pastWindow(i, now) ? (
                        <Typography
                          variant="caption"
                          data-testid={`plan-list-past-${i.orderId}`}
                          sx={{ display: 'block', mt: 0.5, color: 'warning.dark' }}
                          data-tip="Okno zlecenia minęło, a TMS nie zamknął zlecenia (status bez „zrealizowane”). Sprawdź status w TMS."
                        >
                          po oknie
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ ...CELL_SX, minWidth: 110 }}>{routeText(i)}</TableCell>
                    <TableCell sx={{ ...CELL_SX, minWidth: 130 }}>
                      <WindowCell item={i} />
                    </TableCell>
                    <TableCell sx={{ ...CELL_SX, minWidth: 110 }}>
                      <span>{rangeText(i.occupancyFrom, i.occupancyTo)}</span>
                      {i.occupancyBasis === 'window' ? (
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                          całe okno zlecenia (trasa nieznana)
                        </Typography>
                      ) : null}
                      {late ? (
                        <Typography
                          variant="caption"
                          data-testid={`plan-list-late-${i.orderId}`}
                          sx={{ display: 'block', color: 'warning.dark' }}
                        >
                          {`szac. koniec ${shortDateTime(late.to)} (+${durationText(
                            new Date(late.to).getTime() - new Date(late.from).getTime(),
                          )} po oknie)`}
                        </Typography>
                      ) : null}
                      {i.tmsPlanSegment && i.occupiedUntil ? (
                        <Typography
                          variant="caption"
                          data-testid={`plan-list-tms-${i.orderId}`}
                          sx={{ display: 'block', color: 'text.secondary' }}
                        >
                          {`zajęty do ${shortDateTime(i.occupiedUntil)} (wg planu TMS)`}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ ...CELL_SX, minWidth: 110 }}>
                      <SourceCell item={i} />
                      <AssignedByLine item={i} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      ) : null}
      {hiddenPlanned > 0 ? (
        <Button size="small" onClick={() => setShowAllPlanned((v) => !v)} sx={{ mt: 0.5 }}>
          {showAllPlanned
            ? `Pokaż tylko ${PLANNED_SHOWN} najbliższe zaplanowane`
            : `Pokaż dalsze zaplanowane (${hiddenPlanned})`}
        </Button>
      ) : null}
      {hiddenDone > 0 ? (
        <Button size="small" onClick={() => setShowEarlierDone((v) => !v)} sx={{ mt: 0.5 }}>
          {showEarlierDone
            ? 'Ukryj wcześniejsze zrealizowane'
            : `Pokaż wcześniejsze zrealizowane (${hiddenDone})`}
        </Button>
      ) : null}
    </TipLayer>
  );
};

/** Podpis grupy na pasku narzędzi — jak w prototypie („Oś czasu”, „Skala”, „Widok”). */
const ToolGroup = ({ caption, children }: { caption: string; children: React.ReactNode }) => (
  <Box>
    <Typography
      variant="caption"
      sx={{ display: 'block', mb: 0.5, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6 }}
    >
      {caption}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>{children}</Box>
  </Box>
);

const SCALE_LABEL = (d: ScaleDays) => (d === 1 ? '1 dzień' : `${d} dni`);

/** Legenda osi — w kolejności, w jakiej rzeczy stoją na osi. */
const GanttLegend = ({
  segments,
  driverWeeks,
  tmsPlan = false,
}: {
  segments: boolean;
  driverWeeks: boolean;
  /** Odcinek „wg planu TMS” — gdy któraś pozycja go ma (12.10.2026). */
  tmsPlan?: boolean;
}) => {
  // Nawiasy Z/R zamiast okien (24.10.2026, S-24.10-05).
  const brackets = useFeature(GANTT_WINDOW_BRACKETS);
  return (
  <Box sx={{ mt: 1 }} data-testid="gantt-legend">
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
      <LegendSwatch sx={PHASE_FILL.inProgress} label="w trakcie" />
      <LegendSwatch sx={PHASE_FILL.planned} label="zaplanowane" />
      <LegendSwatch sx={PHASE_FILL.done} label="zakończone" />
      {brackets ? (
        <BracketLegend />
      ) : (
        <>
          <LegendSwatch
            sx={{ bgcolor: 'transparent', borderStyle: 'dashed', borderColor: 'text.disabled', width: 28 }}
            label="okno zlecenia od klienta"
          />
          <LegendSwatch
            sx={(theme: Theme) => ({ bgcolor: `${theme.palette.success.main}38`, borderColor: theme.palette.success.light })}
            label="Z / R — okno załadunku i rozładunku z planu TMS"
          />
        </>
      )}
      <LegendSwatch
        sx={{ bgcolor: 'background.paper', borderStyle: 'dashed', borderColor: 'success.dark' }}
        label="wariant (wstępne wskazanie)"
      />
      <LegendSwatch
        sx={{ bgcolor: 'background.paper', borderStyle: 'dashed', borderColor: 'error.main' }}
        label="wariant zlecenia obsadzonego innym pojazdem"
      />
      <LegendSwatch
        sx={{ backgroundImage: `repeating-linear-gradient(135deg, ${WARNING_ORANGE} 0 4px, #f7a65a 4px 8px)`, border: 0 }}
        label="spóźnienie po oknie rozładunku (szacunek)"
      />
      {tmsPlan ? (
        <LegendSwatch
          sx={[{ width: 24 }, (theme: Theme) => tmsPlanFill(theme)] as SxProps<Theme>}
          label="wg planu TMS — plan kończy zlecenie później niż szacunek (tylko informacja)"
        />
      ) : null}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <WarningAmberRoundedIcon sx={{ fontSize: 16, color: WARNING_ORANGE }} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          alert do potwierdzenia na karcie zlecenia
        </Typography>
      </Box>
      <LegendSwatch sx={{ bgcolor: 'error.main', width: 2, border: 0 }} label="teraz" />
    </Box>
    {segments ? (
      <Box
        data-testid="plan-legend-segments"
        sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mt: 0.5 }}
      >
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Odcinki szacunku (w kolorze wiersza):
        </Typography>
        {LEGEND_SEGMENTS.map(({ kind, label }) => (
          <LegendSwatch
            key={kind}
            sx={[{ border: 0, width: 24 }, (theme: Theme) => segmentFill(theme, 'inProgress', kind)] as SxProps<Theme>}
            label={label}
          />
        ))}
      </Box>
    ) : null}
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mt: 0.5 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        Aktywność kierowcy:
      </Typography>
      {(['drive', 'work', 'break', 'rest', 'unknown'] as const).map((k) => (
        <LegendSwatch
          key={k}
          sx={{ bgcolor: ACTIVITY_COLORS[k].bg, borderColor: 'rgba(0,0,0,0.18)' }}
          label={k === 'unknown' ? 'dyspozycyjność, czynność nieznana' : ACTIVITY_LABELS[k].toLowerCase()}
        />
      ))}
      <LegendSwatch
        sx={{ bgcolor: ACTIVITY_COLORS.drive.bg, opacity: 0.55, borderStyle: 'dashed', borderColor: 'text.primary' }}
        label="plan z obsadzeń (szacunek)"
      />
      <LegendSwatch sx={{ bgcolor: '#546e7a', border: 0 }} label="tygodniowy czas jazdy (limit 56 h)" />
      {driverWeeks ? (
        <>
          <LegendSwatch sx={{ bgcolor: 'background.paper', borderColor: 'info.main' }} label="kierowca wg harmonogramu" />
          <LegendSwatch
            sx={{ bgcolor: 'background.paper', borderStyle: 'dotted', borderColor: 'divider' }}
            label="kierowca przepisany z wcześniejszego miesiąca (tymczasowo)"
          />
        </>
      ) : null}
    </Box>
  </Box>
  );
};

const VehicleSchedulePanel = ({ now }: { now?: Date }) => {
  const record = useRecordContext<RaRecord>();
  const { preview } = useCarCard();
  // Makieta FTL, Etap 1 (15.10.2026): wiersz „Warianty” i skala 1/3/7/14/21/28
  // (start od 14 dni, jak we wzorcu); wyłączony — jak w prototypie z 07.10.
  const variantsLane = useFeature(VEHICLE_VARIANTS_LANE);
  const scale = scaleOptions(variantsLane);
  const [prefs, setPrefs] = useState(() => readGanttPrefs(undefined, scale));
  const [startKey, setStartKey] = useState(() => homeStartKey(clientDayKey(now ?? new Date()), prefs.days));
  const [loadedPlan, setPlan] = useState<VehiclePlan | null>(null);
  // „Zajęte do” wg planu TMS tylko z przełącznikiem (12.10.2026).
  // Kandydatura do zlecenia w realizacji w TMS nie jest wariantem (S-24.10-06).
  const runningNotVariant = useFeature(RUNNING_ORDER_NOT_VARIANT);
  const tmsPlanOn = useFeature(OCCUPIED_UNTIL_TMS_PLAN);
  const plan = loadedPlan && !tmsPlanOn ? withoutTmsPlan(loadedPlan) : loadedPlan;
  const [activity, setActivity] = useState<DriverActivity | null>(null);
  const [activityError, setActivityError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [highlight, setHighlight] = useState<number | null>(null);
  const id = record?.id;
  const days = prefs.days;

  useEffect(() => {
    if (id === undefined) return undefined;
    let cancelled = false;
    const keys = viewKeys(startKey, days);
    setLoading(true);
    setLoadError(null);
    setActivity(null);
    setActivityError(null);
    fetchVehiclePlan(id, keys)
      .then((p) => {
        if (!cancelled) setPlan(p);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(toErrorMessage(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    // Aktywność kierowcy osobno: jej błąd nie zasłania planu.
    fetchDriverActivity(id, keys)
      .then((a) => {
        if (!cancelled) setActivity(a);
      })
      .catch((e) => {
        if (!cancelled) setActivityError(toErrorMessage(e));
      });
    return () => {
      cancelled = true;
    };
  }, [id, startKey, days]);

  if (!record) return null;

  const savePrefs = (next: typeof prefs) => {
    setPrefs(next);
    writeGanttPrefs(next);
  };
  const setDays = (next: ScaleDays) => {
    if (next === days) return;
    setStartKey((k) => rescaleStartKey(k, days, next));
    savePrefs({ ...prefs, days: next });
  };
  const setDetail = (detail: GanttDetail) => savePrefs({ ...prefs, detail });
  const shift = (delta: number) => setStartKey((k) => shiftDayKey(k, delta));
  const window = viewWindow(startKey, days);
  const assignments = plan?.items.filter((i) => i.kind === 'assignment') ?? [];
  const candidates =
    plan?.items.filter((i) => i.kind === 'candidate' && !(runningNotVariant && runningInTms(i))) ?? [];

  return (
    <SectionCard
      title={`Harmonogram (${plural(assignments.length, 'obsadzenie', 'obsadzenia', 'obsadzeń')}, ${plural(candidates.length, 'wariant', 'warianty', 'wariantów')})`}
    >
      {/* Szybkie dymki przycisków osi (prototyp, zmiana 9) — ten sam mechanizm
          co na osi, także z klawiatury; wcześniej podpowiedź przeglądarki
          (`title`) po sekundzie. */}
      <TipLayer>
      <Box
        sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', columnGap: 3.5, rowGap: 1.5, mb: 1.75 }}
        data-testid="gantt-toolbar"
      >
        <ToolGroup caption="Oś czasu">
          <IconButton
            size="small"
            aria-label="Przewiń oś czasu wstecz"
            data-tip={scrollStep(days) === 1 ? 'Wstecz o dzień' : 'Wstecz o tydzień'}
            onClick={() => shift(-scrollStep(days))}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
          >
            <ChevronLeftRoundedIcon />
          </IconButton>
          <Button
            size="small"
            variant="outlined"
            data-tip="Wróć do bieżącego okna — dziś na środku osi"
            onClick={() => setStartKey(homeStartKey(clientDayKey(now ?? new Date()), days))}
          >
            Dziś
          </Button>
          <IconButton
            size="small"
            aria-label="Przewiń oś czasu naprzód"
            data-tip={scrollStep(days) === 1 ? 'Naprzód o dzień' : 'Naprzód o tydzień'}
            onClick={() => shift(scrollStep(days))}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
          >
            <ChevronRightRoundedIcon />
          </IconButton>
          <Typography
            variant="body2"
            aria-live="polite"
            data-testid="gantt-range"
            sx={{ ml: 1, fontWeight: 500, whiteSpace: 'nowrap' }}
          >
            {rangeLabel(startKey, days)}
          </Typography>
          {loading ? <CircularProgress size={18} /> : null}
        </ToolGroup>
        <ToolGroup caption="Skala">
          <ToggleButtonGroup
            size="small"
            exclusive
            color="primary"
            value={days}
            aria-label="Zakres okna czasowego"
            onChange={(_, v: unknown) => {
              if (isScale(v, scale.levels)) setDays(v);
            }}
          >
            {scale.levels.map((d) => (
              <ToggleButton key={d} value={d} sx={{ px: 1.25, py: 0.5, textTransform: 'none' }}>
                {SCALE_LABEL(d)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </ToolGroup>
        <ToolGroup caption="Widok">
          <ToggleButtonGroup
            size="small"
            exclusive
            color="primary"
            value={prefs.detail}
            aria-label="Szczegółowość widoku"
            onChange={(_, v: unknown) => {
              if (v === 'simple' || v === 'full') setDetail(v);
            }}
          >
            <ToggleButton
              value="simple"
              data-tip="Okna załadunku i rozładunku nałożone na siebie, na nich pasek realizacji"
              sx={{ px: 1.25, py: 0.5, textTransform: 'none' }}
            >
              Uproszczony
            </ToggleButton>
            <ToggleButton
              value="full"
              data-tip="Osobno: okno załadunku, realizacja, okno rozładunku"
              sx={{ px: 1.25, py: 0.5, textTransform: 'none' }}
            >
              Pełny
            </ToggleButton>
          </ToggleButtonGroup>
        </ToolGroup>
      </Box>
      </TipLayer>
      {loadError ? <Alert severity="error">{loadError}</Alert> : null}
      {plan ? (
        <>
          {plan.months.length > 0 ? (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              {/* Sam okres i polski status okna miesięcznego (poprawa brzmienia 01.10.2026). */}
              {plan.months.map((m) => (
                <Chip
                  key={m.id}
                  size="small"
                  variant="outlined"
                  color={m.unavailable ? 'warning' : 'success'}
                  label={`${fmtDay(m.from)} – ${fmtDay(m.to)}: ${scheduleStatusLabel(m.status)}`}
                  title="Status pojazdu w harmonogramie miesięcznym"
                />
              ))}
            </Box>
          ) : null}
          <VehicleGantt
            window={window}
            days={days}
            detail={prefs.detail}
            items={plan.items}
            months={plan.months}
            driverWeeks={plan.driverWeeks ?? []}
            activity={activity}
            activityError={activityError}
            now={now}
            highlight={highlight}
            onHighlight={setHighlight}
            preview={preview}
            variantsLane={variantsLane}
            onShowDate={(d) => setStartKey(homeStartKey(clientDayKey(d), days))}
          />
          <GanttLegend
            segments={plan.items.some(hasSegments)}
            tmsPlan={plan.items.some((i) => i.tmsPlanSegment)}
            driverWeeks={(plan.driverWeeks ?? []).length > 0}
          />
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
            {plan.occupancyNote}
            {plan.segmentsNote ? ` ${plan.segmentsNote}` : ''}
            {plan.tmsPlanNote ? ` ${plan.tmsPlanNote}` : ''}
            {activity?.note ? ` ${activity.note}` : ''} Godziny w czasie polskim.
          </Typography>
          {plan.items.length === 0 ? (
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              Brak obsadzeń i wariantów w okresie {fmtDay(plan.from)} – {fmtDay(plan.to)}.
            </Typography>
          ) : (
            <Box sx={{ mt: 1 }}>
              <PlanList items={plan.items} highlight={highlight} onHighlight={setHighlight} now={now ?? new Date()} />
            </Box>
          )}
        </>
      ) : null}
    </SectionCard>
  );
};

/** Stan panelu należy do rekordu — przejście na inny rekord zaczyna od zera (`recordScoped`). */
export const VehicleSchedule = recordScoped(VehicleSchedulePanel);
